import React, { useState, useEffect, Fragment } from 'react';
import { useHistory, Switch, Route, useRouteMatch } from 'react-router-dom';

// Moment
import moment from 'moment';

// Notistack
import { useSnackbar } from 'notistack';

// Material-UI
import { Grid, Divider, Button, Checkbox, Dialog, DialogContent, DialogActions, MenuItem } from '@material-ui/core';
import { Search as SearchIcon, Cancel as CancelIcon, ViewWeek as ColumnIcon, UnfoldMore as OrderIcon, FileCopy as CopyIcon } from '@material-ui/icons';

// Services
import { getSubscriptions, getIndexPreferences, updateIndexPreferences } from 'services/requests';
import { getStoredUser } from 'services/storage';
import { subscriptionStatuses, membershipStatuses, billStates, billStatuses} from 'services/constants';
import {
    getLabelStatus, getColorStatus, getFormattedDate, getMonthYearDate,
    getFormattedCreatedAt, getFilterOrder, getAmountTTC, getSubscriptionOriginLabel,
} from 'services/helpers';
import useMediaQueries from 'services/media';

// Components
import Spinner from 'components/spinner';
import Error from 'components/error';
import Pagination from 'components/pagination';
import InputText from 'components/input-text';
import Tooltip from 'components/tooltip';
import SelectCreatable from 'components/select-creatable';
import Select from 'components/react-select';
import SelectOutlined from 'components/select-outlined';

// Views
import Bill from '../../common/bill';
import AlterrenatImport from './alterrenat-import';
import UpdateSubscription from './update-subscription';
import SubscriptionsExport from './subscriptions-export';
import StopSubscription from './stop-subscription';
import GroupFilter from 'views/common/group-filter';

// Selects
import SelectYesOrNo from 'selects/select-yes-or-no';
import SelectAdherentType from 'selects/select-adherent-type';
import SelectFederatedAssociations from 'selects/select-federated-associations';
import SelectBillState from 'selects/select-bill-state';
import SelectBillStatuses from 'selects/select-bill-statuses';

// Columns
import { SubscriptionStatusHeader, SubscriptionStatusFilter, SubscriptionStatusValue } from 'views/columns/subscription-status';
import { ClientCodeHeader, ClientCodeFilter, ClientCodeValue } from 'views/columns/client-code';
import { ExternalClientCodeHeader, ExternalClientCodeFilter, ExternalClientCodeValue } from 'views/columns/external-client-code';
import { SubscriptionTypeHeader, SubscriptionTypeFilter, SubscriptionTypeValue } from 'views/columns/subscription-type';
import { MembershipStatusHeader, MembershipStatusFilter, MembershipStatusValue } from 'views/columns/membership-status';
import { LastNameHeader, LastNameFilter, LastNameValue } from 'views/columns/last-name';
import { FirstNameHeader, FirstNameFilter, FirstNameValue } from 'views/columns/first-name';
import { CompanyNameHeader, CompanyNameFilter, CompanyNameValue } from 'views/columns/company-name';
import { MobilePhoneHeader, MobilePhoneFilter, MobilePhoneValue } from 'views/columns/mobile-phone';
import { EmailHeader, EmailFilter, EmailValue } from 'views/columns/email';
import { FrenchDepartmentHeader, FrenchDepartmentFilter, FrenchDepartmentValue } from 'views/columns/french-department';
import { CityHeader, CityFilter, CityValue } from 'views/columns/city';
import { PostalCodeHeader, PostalCodeFilter, PostalCodeValue } from 'views/columns/postal-code';
import { AddressHeader, AddressFilter, AddressValue } from 'views/columns/address';
import { AddressDetailsHeader, AddressDetailsFilter, AddressDetailsValue } from 'views/columns/address-details';
import { CountryHeader, CountryFilter, CountryValue } from 'views/columns/country';
import { PhoneHeader, PhoneFilter, PhoneValue } from 'views/columns/phone';
import { OtherPhoneHeader, OtherPhoneFilter, OtherPhoneValue } from 'views/columns/other-phone';
import { UserCategoryHeader, UserCategoryFilter, UserCategoryValue } from 'views/columns/user-category';
import { BirthDateHeader, BirthDateFilter, BirthDateValue } from 'views/columns/birth-date';

// ----------------------------------------------------------------------------------------------- \\
// ------------------------------------- SUBSCRIPTIONS ROUTER ------------------------------------ \\
// ----------------------------------------------------------------------------------------------- \\

const SubscriptionsRouter = () => {
    const match = useRouteMatch();

    return (
        <Switch>
            <Route exact path={match.path}>
                <Subscriptions />
            </Route>
            <Route path={`${match.path}/:billId`}>
                <Bill />
            </Route>
        </Switch>
    );
};

// ----------------------------------------------------------------------------------------------- \\
// ---------------------------------------- SUBSCRIPTIONS ---------------------------------------- \\
// ----------------------------------------------------------------------------------------------- \\

const Subscriptions = () => {
    const history = useHistory();
    const { mediaMD } = useMediaQueries();
    const { enqueueSnackbar } = useSnackbar();
    const storedUser = getStoredUser();
    const storedUserId = storedUser ? storedUser.id : null;
    const storedFilters = JSON.parse(sessionStorage.getItem('subscriptions-filters'));

    const [state, setState] = useState({
        loadingPreferences: true,
        loading: false,
        error: false,
        subscriptions: [],
        offset: 0,
        limit: 20,
        total_count: 0,
        copy_emails: false,

        // Update
        openUpdate: false,
        subscription: null,

        // Links
        openLinks: false,
        billId: null,

        // Stop
        openStop: false,

        // Manage Columns
        loadingColumns: false,
        openColumns: false,
        enabled_subscription_id: true,
        enabled_subscription_status: true,
        enabled_client_code: true,
        enabled_external_client_code: true,
        enabled_subscription_type: true,
        enabled_membership_status: true,
        enabled_last_name: true,
        enabled_first_name: true,
        enabled_company_name: true,
        enabled_mobile_phone: true,
        enabled_email: true,
        enabled_french_department: true,
        enabled_city: true,
        enabled_postal_code: false,
        enabled_address: false,
        enabled_address_details: false,
        enabled_country: false,
        enabled_phone: false,
        enabled_other_phone: false,
        enabled_subscription_email: false,
        enabled_subscription_expiration: false,
        enabled_month_year_subscription_expiration: false,
        enabled_user_category: false,
        enabled_birth_date: false,
        enabled_federated_association: false,
        enabled_membership_expiration: false,
        enabled_membership_email: false,
        enabled_bill_email: false,
        enabled_adherent_type: false,
        enabled_last_number_received: false,
        enabled_last_number_to_receive: false,
        enabled_created_at: false,
        enabled_bill_state: false,
        enabled_bill_reference: false,
        enabled_bill_status: false,
        enabled_bill_total_amount: false,
        enabled_is_alterrenat: false,
        enabled_renew_status: false,
        enabled_origin: false,
        enabled_start_date: false,

        // Filters
        order_by: storedFilters?.order_by || '',
        order: storedFilters?.order || 'desc',
        subscription_id: storedFilters?.subscription_id || '',
        subscription_statuses: storedFilters?.subscription_statuses || [],
        client_code: storedFilters?.client_code || '',
        external_client_code: storedFilters?.external_client_code || '',
        subscription_types: storedFilters?.subscription_types || [],
        membership_statuses: storedFilters?.membership_statuses || [],
        last_name: storedFilters?.last_name || '',
        first_name: storedFilters?.first_name || '',
        company_name: storedFilters?.company_name || '',
        mobile_phone: storedFilters?.mobile_phone || '',
        email: storedFilters?.email || '',
        french_departments: storedFilters?.french_departments || [],
        cities: storedFilters?.cities || [],
        postal_codes: storedFilters?.postal_codes || [],
        address: storedFilters?.address || '',
        address_details: storedFilters?.address_details || '',
        countries: storedFilters?.countries || [],
        phone: storedFilters?.phone || '',
        other_phone: storedFilters?.other_phone || '',
        subscription_email: storedFilters?.subscription_email || '',
        subscription_expiration: storedFilters?.subscription_expiration || '',
        month_year_subscription_expirations: storedFilters?.month_year_subscription_expirations || [],
        user_categories: storedFilters?.user_categories || [],
        birth_date: storedFilters?.birth_date || '',
        federated_associations: storedFilters?.federated_associations || [],
        membership_expiration: storedFilters?.membership_expiration || '',
        membership_email: storedFilters?.membership_email || '',
        bill_email: storedFilters?.bill_email || '',
        adherent_type: storedFilters?.adherent_type != null ? storedFilters?.adherent_type : -1,
        last_number_received: storedFilters?.last_number_received || '',
        last_number_to_receive: storedFilters?.last_number_to_receive || '',
        created_at: storedFilters?.created_at || '',
        bill_state: storedFilters?.bill_state != null ? storedFilters?.bill_state : -1,
        bill_reference: storedFilters?.bill_reference || '',
        bill_statuses: storedFilters?.bill_statuses || [],
        bill_total_amount: storedFilters?.bill_total_amount || '',
        user_list_ids: storedFilters?.user_list_ids || [],
        is_alterrenat: storedFilters?.is_alterrenat != null ? storedFilters?.is_alterrenat : -1,
        renew_status: storedFilters?.renew_status || null,
        origin: storedFilters?.origin != null ? storedFilters?.origin : -1,
        start_date: storedFilters?.start_date || '',
    });

    function onSearch() {
        setState({ ...state, loading: true, error: false, offset: 0 });
    }

    function onSelectSearch(item, value) {
        setState({ ...state, [item]: value, loading: true, error: false, offset: 0 });
    }

    function cancelSearch() {
        sessionStorage.removeItem('subscriptions-filters');
        setState({
            ...state,
            loading: true,
            error: false,
            offset: 0,
            subscription_id: '',
            subscription_statuses: [],
            order_by: '',
            order: 'desc',
            client_code: '',
            external_client_code: '',
            subscription_types: [],
            membership_statuses: [],
            last_name: '',
            first_name: '',
            company_name: '',
            mobile_phone: '',
            email: '',
            french_departments: [],
            cities: [],
            postal_codes: [],
            address: '',
            address_details: '',
            countries: [],
            phone: '',
            other_phone: '',
            subscription_email: '',
            subscription_expiration: '',
            month_year_subscription_expirations: [],
            user_categories: [],
            birth_date: '',
            federated_associations: [],
            membership_expiration: '',
            membership_email: '',
            bill_email: '',
            adherent_type: -1,
            last_number_received: '',
            last_number_to_receive: '',
            created_at: '',
            bill_state: -1,
            bill_reference: '',
            bill_statuses: [],
            bill_total_amount: '',
            user_list_ids: [],
            is_alterrenat: -1,
            renew_status: null,
            copy_emails: false,
            origin: -1,
            start_date: '',
        });
    }

    useEffect(() => {
        if (state.loadingPreferences) {
            getIndexPreferences(storedUserId, {
                name: 'subscriptions',
            }).then(res => {
                if (res.status === 200) {

                    const indexPreferences = res.data.data.indexPreferences;

                    if (indexPreferences != null && indexPreferences.length > 0) {
                        const columns = JSON.parse(indexPreferences[0].columns);
                        setState({
                            ...state,
                            loadingPreferences: false,
                            loading: true,
                            enabled_subscription_id: columns.enabled_subscription_id,
                            enabled_subscription_status: columns.enabled_subscription_status,
                            enabled_client_code: columns.enabled_client_code,
                            enabled_external_client_code: columns.enabled_external_client_code,
                            enabled_subscription_type: columns.enabled_subscription_type,
                            enabled_membership_status: columns.enabled_membership_status,
                            enabled_last_name: columns.enabled_last_name,
                            enabled_first_name: columns.enabled_first_name,
                            enabled_company_name: columns.enabled_company_name,
                            enabled_mobile_phone: columns.enabled_mobile_phone,
                            enabled_email: columns.enabled_email,
                            enabled_french_department: columns.enabled_french_department,
                            enabled_city: columns.enabled_city,
                            enabled_postal_code: columns.enabled_postal_code,
                            enabled_address: columns.enabled_address,
                            enabled_address_details: columns.enabled_address_details,
                            enabled_country: columns.enabled_country,
                            enabled_phone: columns.enabled_phone,
                            enabled_other_phone: columns.enabled_other_phone,
                            enabled_subscription_email: columns.enabled_subscription_email,
                            enabled_subscription_expiration: columns.enabled_subscription_expiration,
                            enabled_month_year_subscription_expiration: columns.enabled_month_year_subscription_expiration,
                            enabled_user_category: columns.enabled_user_category,
                            enabled_birth_date: columns.enabled_birth_date,
                            enabled_federated_association: columns.enabled_federated_association,
                            enabled_membership_expiration: columns.enabled_membership_expiration,
                            enabled_membership_email: columns.enabled_membership_email,
                            enabled_bill_email: columns.enabled_bill_email,
                            enabled_adherent_type: columns.enabled_adherent_type,
                            enabled_last_number_received: columns.enabled_last_number_received,
                            enabled_last_number_to_receive: columns.enabled_last_number_to_receive,
                            enabled_created_at: columns.enabled_created_at,
                            enabled_bill_state: columns.enabled_bill_state,
                            enabled_bill_reference: columns.enabled_bill_reference,
                            enabled_bill_status: columns.enabled_bill_status,
                            enabled_bill_total_amount: columns.enabled_bill_total_amount,
                            enabled_is_alterrenat: columns.enabled_is_alterrenat,
                            enabled_renew_status: columns.enabled_renew_status,
                            enabled_origin: columns.enabled_origin,
                            enabled_start_date: columns.enabled_start_date,
                        });
                    }
                    else {
                        setState({ ...state, loadingPreferences: false, loading: true });
                    }

                }
                else {
                    console.log(res);
                    setState({ ...state, loadingPreferences: false, loading: true });
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.loadingPreferences]);

    useEffect(() => {
        if (state.loading) {

            sessionStorage.setItem('subscriptions-filters', JSON.stringify({
                order_by: state.order_by,
                order: state.order,
                subscription_id: state.subscription_id,
                subscription_statuses: state.subscription_statuses,
                client_code: state.client_code,
                external_client_code: state.external_client_code,
                subscription_types: state.subscription_types,
                membership_statuses: state.membership_statuses,
                last_name: state.last_name,
                first_name: state.first_name,
                company_name: state.company_name,
                mobile_phone: state.mobile_phone,
                email: state.email,
                french_departments: state.french_departments,
                cities: state.cities,
                postal_codes: state.postal_codes,
                address: state.address,
                address_details: state.address,
                countries: state.countries,
                phone: state.phone,
                other_phone: state.other_phone,
                subscription_email: state.subscription_email,
                subscription_expiration: state.subscription_expiration,
                month_year_subscription_expirations: state.month_year_subscription_expirations,
                user_categories: state.user_categories,
                birth_date: state.birth_date,
                federated_associations: state.federated_associations,
                membership_expiration: state.membership_expiration,
                membership_email: state.membership_email,
                bill_email: state.bill_email,
                adherent_type: state.adherent_type,
                last_number_received: state.last_number_received,
                last_number_to_receive: state.last_number_to_receive,
                created_at: state.created_at,
                bill_state: state.bill_state,
                bill_reference: state.bill_reference,
                bill_statuses: state.bill_statuses,
                bill_total_amount: state.bill_total_amount,
                user_list_ids: state.user_list_ids,
                is_alterrenat: state.is_alterrenat,
                renew_status: state.renew_status,
                origin: state.origin,
                start_date: state.start_date,
            }));

            getSubscriptions({
                offset: state.offset,
                limit: state.limit,
                order_by: state.order_by,
                order: state.order,
                subscription_id: state.subscription_id,
                subscription_statuses: state.subscription_statuses ? state.subscription_statuses.map(s => s.value) : null,
                client_code: state.client_code,
                external_client_code: state.external_client_code,
                subscription_type_ids: state.subscription_types ? state.subscription_types.map(s => s.value) : null,
                membership_statuses: state.membership_statuses ? state.membership_statuses.map(s => s.value) : null,
                last_name: state.last_name,
                first_name: state.first_name,
                company_name: state.company_name,
                mobile_phone: state.mobile_phone,
                email: state.email,
                french_department_ids: state.french_departments ? state.french_departments.map(d => d.value) : null,
                cities: state.cities ? state.cities.map(c => c.value) : null,
                postal_codes: state.postal_codes ? state.postal_codes.map(c => c.value) : null,
                address: state.address,
                address_details: state.address_details,
                country_ids: state.countries ? state.countries.map(c => c.value) : null,
                phone: state.phone,
                other_phone: state.other_phone,
                subscription_email: state.subscription_email,
                subscription_expiration: state.subscription_expiration,
                month_year_subscription_expirations: state.month_year_subscription_expirations ? state.month_year_subscription_expirations.map(e => e.value) : null,
                user_category_ids: state.user_categories ? state.user_categories.map(c => c.value) : null,
                birth_date: state.birth_date,
                federated_association_ids: state.federated_associations ? state.federated_associations.map(a => a.value) : null,
                membership_expiration: state.membership_expiration,
                membership_email: state.membership_email,
                bill_email: state.bill_email,
                adherent_type: state.adherent_type,
                last_number_received: state.last_number_received,
                last_number_to_receive: state.last_number_to_receive,
                created_at: state.created_at,
                bill_state: state.bill_state,
                bill_reference: state.bill_reference,
                bill_statuses: state.bill_statuses ? state.bill_statuses.map(s => s.value) : null,
                bill_total_amount: state.bill_total_amount,
                user_list_ids: state.user_list_ids,
                is_alterrenat: state.is_alterrenat,
                renew_status: state.renew_status ? state.renew_status.value : null,
                copy_emails: state.copy_emails,
                origin: state.origin,
                start_date: state.start_date,
            }).then(res => {
                if (res.status === 200) {
                    if (state.copy_emails) {

                        navigator.clipboard.writeText(res?.data?.data?.emails).then(function() {
                            enqueueSnackbar('Emails copiés avec succès !', { variant: 'success' });
                        }, function(err) {
                            enqueueSnackbar('Copie des emails échoués', { variant: 'error' });
                        });

                        setState({
                            ...state,
                            loading: false,
                            copy_emails: false,
                        });
                    }
                    else {
                        setState({
                            ...state,
                            loading: false,
                            offset: res.data.data.offset,
                            limit: res.data.data.limit,
                            total_count: res.data.data.totalCount,
                            subscriptions: res.data.data.subscriptions,
                        });
                    }
                }
                else {
                    console.log(res);
                    setState({ ...state, loading: false, error: true });
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.loading]);

    if (state.loadingPreferences) return <Spinner />;

    return (
        <>
            {/* -------------------- SUBHEADER -------------------- */}
            <Grid container alignItems="center" className="subheader">
                <Grid item xs={12} md={4} container justify={mediaMD ? 'flex-start' : 'center'}>
                    <Button variant="contained" onClick={() => history.push('/management')}>
                        Retour
                    </Button>
                </Grid>
                <Grid item xs={12} md={4} container justify="center" style={{ padding: mediaMD ? 0 : 10 }}>
                    Abonnements
                </Grid>
                <Grid item xs={12} md={4} container justify={mediaMD ? 'flex-end' : 'center'}>
                    {(storedUser?.role?.id === 1) && (
                        <Button
                            onClick={() => setState({ ...state, openImport: true })}
                            variant="contained" color="primary"
                        >
                            Import Abos Spéciaux
                        </Button>
                    )}
                </Grid>
            </Grid>
            {/* -------------------- TOOLBAR -------------------- */}
            <Grid container justify="space-between" alignItems="center" className="toolbar">
                <div>
                    <Tooltip title="Chercher"
                        item={(
                            <Button onClick={onSearch} variant="contained">
                                <SearchIcon />
                            </Button>
                        )}
                    />
                    <Tooltip title="Annuler la recherche"
                        item={(
                            <Button onClick={cancelSearch} variant="contained" style={{ marginLeft: 5 }}>
                                <CancelIcon />
                            </Button>
                        )}
                    />
                    <Tooltip title="Gérer les colonnes"
                        item={(
                            <Button
                                onClick={() => setState({ ...state, openColumns: true })}
                                variant="contained"
                                style={{ marginLeft: 5 }}
                            >
                                <ColumnIcon />
                            </Button>
                        )}
                    />
                    <Tooltip title="Copier les emails"
                        item={(
                            <Button
                                onClick={() => setState({ ...state, loading: true, copy_emails: true })}
                                variant="contained"
                                style={{ marginLeft: 5 }}
                            >
                                <CopyIcon />
                            </Button>
                        )}
                    />
                    <GroupFilter
                        userListIds={state.user_list_ids}
                        setUserListIds={user_list_ids => onSelectSearch('user_list_ids', user_list_ids)}
                    />
                    <SubscriptionsExport data={state} />
                </div>
                <b>
                    {state.total_count} {state.total_count > 1 ? 'Résultats' : 'Résultat'}
                </b>
            </Grid>
            <div className="main-container" style={{ paddingBottom: 24 }}>
                <div className="main-container-item">
                    <div className="list-sticky">
                        <Headers
                            state={state}
                            setState={setState}
                        />
                        <Filters
                            state={state} setState={setState}
                            onSearch={onSearch}
                            onSelectSearch={onSelectSearch}
                        />
                    </div>
                    <SubscriptionList
                        state={state}
                        setState={setState}
                    />
                </div>
            </div>
            <Divider />
            {/* -------------------- PAGINATION -------------------- */}
            <Grid container justify="flex-end" style={{ padding: 24 }}>
                <Pagination
                    limit={state.limit}
                    total_count={state.total_count}
                    offset={state.offset}
                    setOffset={offset => setState({ ...state, loading: true, offset })}
                />
            </Grid>
            {/* -------------------- COLUMNS -------------------- */}
            {state.openColumns && (
                <ManageColumns
                    state={state}
                    setState={setState}
                    storedUserId={storedUserId}
                />
            )}
            {/* -------------------- LINKS -------------------- */}
            {state.openLinks && (
                <SubscriptionLinks
                    state={state}
                    setState={setState}
                />
            )}
            {/* -------------------- ALTERRENAT IMPORT -------------------- */}
            {state.openImport && (
                <AlterrenatImport
                    onClose={() => setState({ ...state, openImport: false })}
                />
            )}
        </>
    );
};

// ----------------------------------------------------------------------------------------------- \\
// ------------------------------------------- HEADERS ------------------------------------------- \\
// ----------------------------------------------------------------------------------------------- \\

const Headers = ({ state, setState }) => {

    function onSearch(order_by) {
        setState({ ...state, loading: true, error: false, offset: 0, order_by, order: getFilterOrder(state.order) });
    }

    return (
        <div className="list-headers">
            {/* -------------------- SUBSCRIPTION ID -------------------- */}
            {state.enabled_subscription_id && (
                <div
                    className="column-list"
                    style={{ width: 80 }}
                >
                    N°Abo
                </div>
            )}
            {/* -------------------- SUBSCRIPTION STATUS -------------------- */}
            {state.enabled_subscription_status && (
                <SubscriptionStatusHeader />
            )}
            {/* -------------------- CLIENT CODE -------------------- */}
            {state.enabled_client_code && (
                <ClientCodeHeader />
            )}
            {/* -------------------- EXTERNAL CLIENT CODE -------------------- */}
            {state.enabled_external_client_code && (
                <ExternalClientCodeHeader />
            )}
            {/* -------------------- SUBSCRIPTION TYPE -------------------- */}
            {state.enabled_subscription_type && (
                <SubscriptionTypeHeader />
            )}
            {/* -------------------- MEMBERSHIP STATUS -------------------- */}
            {state.enabled_membership_status && (
                <MembershipStatusHeader />
            )}
            {/* -------------------- LAST NAME -------------------- */}
            {state.enabled_last_name && (
                <LastNameHeader />
            )}
            {/* -------------------- FIRST NAME -------------------- */}
            {state.enabled_first_name && (
                <FirstNameHeader />
            )}
            {/* -------------------- COMPANY NAME -------------------- */}
            {state.enabled_company_name && (
                <CompanyNameHeader />
            )}
            {/* -------------------- MOBILE PHONE -------------------- */}
            {state.enabled_mobile_phone && (
                <MobilePhoneHeader />
            )}
            {/* -------------------- EMAIL -------------------- */}
            {state.enabled_email && (
                <EmailHeader />
            )}
            {/* -------------------- FRENCH DEPARTMENT -------------------- */}
            {state.enabled_french_department && (
                <FrenchDepartmentHeader />
            )}
            {/* -------------------- CITY -------------------- */}
            {state.enabled_city && (
                <CityHeader />
            )}
            {/* -------------------- POSTAL CODE -------------------- */}
            {state.enabled_postal_code && (
                <PostalCodeHeader />
            )}
            {/* -------------------- ADDRESS -------------------- */}
            {state.enabled_address && (
                <AddressHeader
                    label="Adresse ABO (n° et voie)"
                />
            )}
            {/* -------------------- ADDRESS DETAILS -------------------- */}
            {state.enabled_address_details && (
                <AddressDetailsHeader
                    label="Adresse ABO (complément)"
                />
            )}
            {/* -------------------- PAYS -------------------- */}
            {state.enabled_country && (
                <CountryHeader />
            )}
            {/* -------------------- PHONE -------------------- */}
            {state.enabled_phone && (
                <PhoneHeader />
            )}
            {/* -------------------- OTHER PHONE -------------------- */}
            {state.enabled_other_phone && (
                <OtherPhoneHeader />
            )}
            {/* -------------------- SUBSCRIPTION EMAIL -------------------- */}
            {state.enabled_subscription_email && (
                <div className="column-list">
                    Email abonnement
                </div>
            )}
            {/* -------------------- START DATE -------------------- */}
            {state.enabled_start_date && (
                <div
                    className="column-list"
                    style={{ width: 120 }}
                >
                    Date début ABO
                </div>
            )}
            {/* -------------------- SUBSCRIPTION EXPIRATION -------------------- */}
            {state.enabled_subscription_expiration && (
                <div
                    className="column-list"
                    style={{ width: 120 }}
                >
                    Date expiration ABO
                </div>
            )}
            {/* -------------------- MONTH YEAR SUBSCRIPTION EXPIRATION -------------------- */}
            {state.enabled_month_year_subscription_expiration && (
                <div className="column-list">
                    Mois / Année d'expiration ABO
                </div>
            )}
            {/* -------------------- USER CATEGORY -------------------- */}
            {state.enabled_user_category && (
                <UserCategoryHeader />
            )}
            {/* -------------------- BIRTH DATE -------------------- */}
            {state.enabled_birth_date && (
                <BirthDateHeader />
            )}
            {/* -------------------- FEDERATED ASSOCIATION -------------------- */}
            {state.enabled_federated_association && (
                <div className="column-list">
                    Association fédérée
                </div>
            )}
            {/* -------------------- MEMBERSHIP EXPIRATION -------------------- */}
            {state.enabled_membership_expiration && (
                <div className="column-list">
                    Date d'expiration adhé.
                </div>
            )}
            {/* -------------------- MEMBERSHIP EMAIL -------------------- */}
            {state.enabled_membership_email && (
                <div className="column-list">
                    Email adhésion
                </div>
            )}
            {/* -------------------- BILL EMAIL -------------------- */}
            {state.enabled_bill_email && (
                <div className="column-list">
                    Email bon de commande
                </div>
            )}
            {/* -------------------- ADHERENT TYPE -------------------- */}
            {state.enabled_adherent_type && (
                <div className="column-list">
                    Type d'adhérent
                </div>
            )}
            {/* -------------------- LAST NUMBER RECEIVED -------------------- */}
            {state.enabled_last_number_received && (
                <div className="column-list">
                    Dernier n° reçu
                </div>
            )}
            {/* -------------------- LAST NUMBER TO RECEIVED -------------------- */}
            {state.enabled_last_number_to_receive && (
                <div className="column-list">
                    Abonné jusqu'au n°
                </div>
            )}
            {/* -------------------- CREATED AT -------------------- */}
            {state.enabled_created_at && (
                <div className="column-list">
                    <Button
                        onClick={() => onSearch('created_at')}
                        style={{ textTransform: 'none', padding: 0, color: 'white' }}
                    >
                        Date BC
                        <OrderIcon style={{ marginLeft: 5 }} />
                    </Button>
                </div>
            )}
            {/* -------------------- BILL STATE -------------------- */}
            {state.enabled_bill_state && (
                <div className="column-list">
                    Etat BC
                </div>
            )}
            {/* -------------------- BILL REFERENCE -------------------- */}
            {state.enabled_bill_reference && (
                <div className="column-list">
                    Référence BC
                </div>
            )}
            {/* -------------------- BILL STATUS -------------------- */}
            {state.enabled_bill_status && (
                <div className="column-list">
                    Statut BC
                </div>
            )}
            {/* -------------------- BILL TOTAL AMOUNT -------------------- */}
            {state.enabled_bill_total_amount && (
                <div className="column-list">
                    Montant total BC
                </div>
            )}
            {/* -------------------- IS ALTERRENAT -------------------- */}
            {state.enabled_is_alterrenat && (
                <div className="column-list">
                    Alterrenat
                </div>
            )}
            {/* -------------------- RENEW STATUS -------------------- */}
            {state.enabled_renew_status && (
                <div className="column-list">
                    Statut renouvellement
                </div>
            )}
            {/* -------------------- ORIGIN -------------------- */}
            {state.enabled_origin && (
                <div
                    className="column-list"
                    style={{ width: 150 }}
                >
                    Origine ABO
                </div>
            )}
        </div>
    );
};

// ----------------------------------------------------------------------------------------------- \\
// ------------------------------------------- FILTERS ------------------------------------------- \\
// ----------------------------------------------------------------------------------------------- \\

const Filters = ({ state, setState, onSearch, onSelectSearch }) => {
    return (
        <div className="list-filters">
            {/* -------------------- SUBSCRIPTION ID -------------------- */}
            {state.enabled_subscription_id && (
                <div
                    className="column-list"
                    style={{ width: 80 }}
                >
                    <InputText
                        label="N°Abo"
                        value={state.subscription_id}
                        onChange={e => setState({ ...state, subscription_id: e.target.value })}
                        onKeyPress={e => e.key === 'Enter' ? onSearch() : null}
                    />
                </div>
            )}
            {/* -------------------- SUBSCRIPTION STATUS -------------------- */}
            {state.enabled_subscription_status && (
                <SubscriptionStatusFilter
                    state={state}
                    onSelectSearch={onSelectSearch}
                />
            )}
            {/* -------------------- CLIENT CODE -------------------- */}
            {state.enabled_client_code && (
                <ClientCodeFilter
                    state={state}
                    setState={setState}
                    onSearch={onSearch}
                />
            )}
            {/* -------------------- EXTERNAL CLIENT CODE -------------------- */}
            {state.enabled_external_client_code && (
                <ExternalClientCodeFilter
                    state={state}
                    setState={setState}
                    onSearch={onSearch}
                />
            )}
            {/* -------------------- SUBSCRIPTION TYPE -------------------- */}
            {state.enabled_subscription_type && (
                <SubscriptionTypeFilter
                    state={state}
                    onSelectSearch={onSelectSearch}
                />
            )}
            {/* -------------------- MEMBERSHIP STATUS -------------------- */}
            {state.enabled_membership_status && (
                <MembershipStatusFilter
                    state={state}
                    onSelectSearch={onSelectSearch}
                />
            )}
            {/* -------------------- LAST NAME -------------------- */}
            {state.enabled_last_name && (
                <LastNameFilter
                    state={state}
                    setState={setState}
                    onSearch={onSearch}
                />
            )}
            {/* -------------------- FIRST NAME -------------------- */}
            {state.enabled_first_name && (
                <FirstNameFilter
                    state={state}
                    setState={setState}
                    onSearch={onSearch}
                />
            )}
            {/* -------------------- COMPANY NAME -------------------- */}
            {state.enabled_company_name && (
                <CompanyNameFilter
                    state={state}
                    setState={setState}
                    onSearch={onSearch}
                />
            )}
            {/* -------------------- MOBILE PHONE -------------------- */}
            {state.enabled_mobile_phone && (
                <MobilePhoneFilter
                    state={state}
                    setState={setState}
                    onSearch={onSearch}
                />
            )}
            {/* -------------------- EMAIL -------------------- */}
            {state.enabled_email && (
                <EmailFilter
                    state={state}
                    setState={setState}
                    onSearch={onSearch}
                />
            )}
            {/* -------------------- DEPARTMENT -------------------- */}
            {state.enabled_french_department && (
                <FrenchDepartmentFilter
                    state={state}
                    onSelectSearch={onSelectSearch}
                />
            )}
            {/* -------------------- CITY -------------------- */}
            {state.enabled_city && (
                <CityFilter
                    state={state}
                    onSelectSearch={onSelectSearch}
                />
            )}
            {/* -------------------- POSTAL CODE -------------------- */}
            {state.enabled_postal_code && (
                <PostalCodeFilter
                    state={state}
                    onSelectSearch={onSelectSearch}
                />
            )}
            {/* -------------------- ADDRESS -------------------- */}
            {state.enabled_address && (
                <AddressFilter
                    label="Adresse ABO (n° et voie)"
                    state={state}
                    setState={setState}
                    onSearch={onSearch}
                />
            )}
            {/* -------------------- ADDRESS DETAILS -------------------- */}
            {state.enabled_address_details && (
                <AddressDetailsFilter
                    label="Adresse ABO (complément)"
                    state={state}
                    setState={setState}
                    onSearch={onSearch}
                />
            )}
            {/* -------------------- PAYS -------------------- */}
            {state.enabled_country && (
                <CountryFilter
                    state={state}
                    onSelectSearch={onSelectSearch}
                />
            )}
            {/* -------------------- PHONE -------------------- */}
            {state.enabled_phone && (
                <PhoneFilter
                    state={state}
                    setState={setState}
                    onSearch={onSearch}
                />
            )}
            {/* -------------------- OTHER PHONE -------------------- */}
            {state.enabled_other_phone && (
                <OtherPhoneFilter
                    state={state}
                    setState={setState}
                    onSearch={onSearch}
                />
            )}
            {/* -------------------- SUBSCRIPTION EMAIL -------------------- */}
            {state.enabled_subscription_email && (
                <div className="column-list">
                    <InputText
                        label="Email abonnement"
                        value={state.subscription_email}
                        onChange={e => setState({ ...state, subscription_email: e.target.value })}
                        onKeyPress={e => e.key === 'Enter' ? onSearch() : null}
                    />
                </div>
            )}
            {/* -------------------- START DATE -------------------- */}
            {state.enabled_start_date && (
                <div
                    className="column-list"
                    style={{ width: 120 }}
                >
                    <InputText
                        label="jj/mm/aaaa"
                        value={state.start_date}
                        onChange={e => setState({ ...state, start_date: e.target.value })}
                        onKeyPress={e => e.key === 'Enter' ? onSearch() : null}
                    />
                </div>
            )}
            {/* -------------------- SUBSCRIPTION EXPIRATION -------------------- */}
            {state.enabled_subscription_expiration && (
                <div
                    className="column-list"
                    style={{ width: 120 }}
                >
                    <InputText
                        label="jj/mm/aaaa"
                        value={state.subscription_expiration}
                        onChange={e => setState({ ...state, subscription_expiration: e.target.value })}
                        onKeyPress={e => e.key === 'Enter' ? onSearch() : null}
                    />
                </div>
            )}
            {/* -------------------- MONTH YEAR SUBSCRIPTION EXPIRATION -------------------- */}
            {state.enabled_month_year_subscription_expiration && (
                <div className="column-list">
                    <SelectCreatable
                        value={state.month_year_subscription_expirations}
                        onChange={month_year_subscription_expirations => onSelectSearch('month_year_subscription_expirations', month_year_subscription_expirations)}
                        placeholder="Mois / Année d'expiration adhé."
                    />
                </div>
            )}
            {/* -------------------- USER CATEGORY -------------------- */}
            {state.enabled_user_category && (
                <UserCategoryFilter
                    state={state}
                    onSelectSearch={onSelectSearch}
                />
            )}
            {/* -------------------- BIRTH DATE -------------------- */}
            {state.enabled_birth_date && (
                <BirthDateFilter
                    state={state}
                    setState={setState}
                    onSearch={onSearch}
                />
            )}
            {/* -------------------- FEDERATED ASSOCIATION -------------------- */}
            {state.enabled_federated_association && (
                <div className="column-list">
                    <SelectFederatedAssociations
                        federatedAssociations={state.federated_associations}
                        setFederatedAssociations={federated_associations => onSelectSearch('federated_associations', federated_associations)}
                    />
                </div>
            )}
            {/* -------------------- MEMBERSHIP EXPIRATION -------------------- */}
            {state.enabled_membership_expiration && (
                <div className="column-list">
                    <InputText
                        label="jj/mm/aaaa"
                        value={state.membership_expiration}
                        onChange={e => setState({ ...state, membership_expiration: e.target.value })}
                        onKeyPress={e => e.key === 'Enter' ? onSearch() : null}
                    />
                </div>
            )}
            {/* -------------------- MEMBERSHIP EMAIL -------------------- */}
            {state.enabled_membership_email && (
                <div className="column-list">
                    <InputText
                        label="Email adhésion"
                        value={state.membership_email}
                        onChange={e => setState({ ...state, membership_email: e.target.value })}
                        onKeyPress={e => e.key === 'Enter' ? onSearch() : null}
                    />
                </div>
            )}
            {/* -------------------- BILL EMAIL -------------------- */}
            {state.enabled_bill_email && (
                <div className="column-list">
                    <InputText
                        label="Email bon de commande"
                        value={state.bill_email}
                        onChange={e => setState({ ...state, bill_email: e.target.value })}
                        onKeyPress={e => e.key === 'Enter' ? onSearch() : null}
                    />
                </div>
            )}
            {/* -------------------- ADHERENT TYPE -------------------- */}
            {state.enabled_adherent_type && (
                <div className="column-list">
                    <SelectAdherentType
                        adherentType={state.adherent_type}
                        setAdherentType={adherent_type => onSelectSearch('adherent_type', adherent_type)}
                    />
                </div>
            )}
            {/* -------------------- LAST NUMBER RECEIVED -------------------- */}
            {state.enabled_last_number_received && (
                <div className="column-list">
                    <InputText
                        label="Dernier n° reçu"
                        value={state.last_number_received}
                        onChange={e => setState({ ...state, last_number_received: e.target.value })}
                        onKeyPress={e => e.key === 'Enter' ? onSearch() : null}
                    />
                </div>
            )}
            {/* -------------------- LAST NUMBER TO RECEIVED -------------------- */}
            {state.enabled_last_number_to_receive && (
                <div className="column-list">
                    <InputText
                        label="Abonné jusqu'au n°"
                        value={state.last_number_to_receive}
                        onChange={e => setState({ ...state, last_number_to_receive: e.target.value })}
                        onKeyPress={e => e.key === 'Enter' ? onSearch() : null}
                    />
                </div>
            )}
            {/* -------------------- CREATED AT -------------------- */}
            {state.enabled_created_at && (
                <div className="column-list">
                    <InputText
                        label="jj/mm/aaaa"
                        value={state.created_at}
                        onChange={e => setState({ ...state, created_at: e.target.value })}
                        onKeyPress={e => e.key === 'Enter' ? onSearch() : null}
                    />
                </div>
            )}
            {/* -------------------- BILL STATE -------------------- */}
            {state.enabled_bill_state && (
                <div className="column-list">
                    <SelectBillState
                        billState={state.bill_state}
                        setBillState={bill_state => onSelectSearch('bill_state', bill_state)}
                    />
                </div>
            )}
            {/* -------------------- BILL REFERENCE -------------------- */}
            {state.enabled_bill_reference && (
                <div className="column-list">
                    <InputText
                        label="Référence BC"
                        value={state.bill_reference}
                        onChange={e => setState({ ...state, bill_reference: e.target.value })}
                        onKeyPress={e => e.key === 'Enter' ? onSearch() : null}
                    />
                </div>
            )}
            {/* -------------------- BILL STATUS -------------------- */}
            {state.enabled_bill_status && (
                <div className="column-list">
                    <SelectBillStatuses
                        statuses={state.bill_statuses}
                        setStatuses={statuses => onSelectSearch('bill_statuses', statuses)}
                    />
                </div>
            )}
            {/* -------------------- BILL TOTAL AMOUNT -------------------- */}
            {state.enabled_bill_total_amount && (
                <div className="column-list">
                    <InputText
                        label="Montant total BC"
                        value={state.bill_total_amount}
                        onChange={e => setState({ ...state, bill_total_amount: e.target.value })}
                        onKeyPress={e => e.key === 'Enter' ? onSearch() : null}
                    />
                </div>
            )}
            {/* -------------------- IS ALTERRENAT -------------------- */}
            {state.enabled_is_alterrenat && (
                <div className="column-list">
                    <SelectYesOrNo
                        label="Alerrenat"
                        value={state.is_alterrenat}
                        onChange={is_alterrenat => onSelectSearch('is_alterrenat', is_alterrenat)}
                    />
                </div>
            )}
            {/* -------------------- RENEW STATUS -------------------- */}
            {state.enabled_renew_status && (
                <div className="column-list">
                    <Select
                        placeholder="Statut renouvellement"
                        value={state.renew_status}
                        onChange={renew_status => onSelectSearch('renew_status', renew_status)}
                        options={[{ value: 'renewed', label: 'Renouvelé' }, { value: 'not_renewed', label: 'Non renouvelé' }]}
                    />
                </div>
            )}
            {/* -------------------- ORIGIN -------------------- */}
            {state.enabled_origin && (
                <div
                    className="column-list"
                    style={{ width: 150 }}
                >
                    <SelectOutlined
                        value={state.origin}
                        onChange={e => onSelectSearch('origin', e.target.value)}
                    >
                        <MenuItem value={-1}>
                            <em>Origine</em>
                        </MenuItem>
                        <MenuItem value="alone">
                            Seul
                        </MenuItem>
                        <MenuItem value="simple">
                            Particulier
                        </MenuItem>
                        <MenuItem value="pro">
                            Professionnel
                        </MenuItem>
                    </SelectOutlined>
                </div>
            )}
        </div>
    );
};

// ----------------------------------------------------------------------------------------------- \\
// -------------------------------------- SUBSCRIPTION LIST -------------------------------------- \\
// ----------------------------------------------------------------------------------------------- \\

const SubscriptionList = ({ state, setState }) => {

    if (state.loading) return <Spinner />;
    if (state.error) return <Error />;

    return (
        <>
            {state.subscriptions.length > 0 && (
                state.subscriptions.map(subscription => (
                    <Fragment key={subscription.id}>
                        <div
                            onClick={() => setState({ ...state, openLinks: true, billId: subscription.billId, subscription })}
                            className="simple-list"
                            style={{ display: 'inline-flex' }}
                        >
                            {/* -------------------- SUBSCRIPTION ID -------------------- */}
                            {state.enabled_subscription_id && (
                                <div
                                    className="column-list"
                                    style={{ width: 80 }}
                                >
                                    #{subscription.id}
                                </div>
                            )}
                            {/* -------------------- SUBSCRIPTION STATUS -------------------- */}
                            {state.enabled_subscription_status && (
                                <SubscriptionStatusValue
                                    value={(
                                        <b className="title-status-list" style={{ color: getColorStatus(subscriptionStatuses, subscription.status) }}>
                                            {getLabelStatus(subscriptionStatuses, subscription.status)}
                                        </b>
                                    )}
                                />
                            )}
                            {/* -------------------- CLIENT CODE -------------------- */}
                            {state.enabled_client_code && (
                                <ClientCodeValue
                                    value={subscription.user?.clientCode}
                                />
                            )}
                            {/* -------------------- EXTERNAL CLIENT CODE -------------------- */}
                            {state.enabled_external_client_code && (
                                <ExternalClientCodeValue
                                    value={subscription.user?.external_client_code}
                                />
                            )}
                            {/* -------------------- SUBSCRIPTION TYPE -------------------- */}
                            {state.enabled_subscription_type && (
                                <SubscriptionTypeValue
                                    value={subscription.subscriptionType?.name}
                                />
                            )}
                            {/* -------------------- MEMBERSHIP STATUS -------------------- */}
                            {state.enabled_membership_status && (
                                <MembershipStatusValue
                                    value={getLabelStatus(membershipStatuses, subscription.user?.membershipStatus)}
                                />
                            )}
                            {/* -------------------- LAST NAME -------------------- */}
                            {state.enabled_last_name && (
                                <LastNameValue
                                    value={subscription?.last_name}
                                />
                            )}
                            {/* -------------------- FIRST NAME -------------------- */}
                            {state.enabled_first_name && (
                                <FirstNameValue
                                    value={subscription?.first_name}
                                />
                            )}
                            {/* -------------------- COMPANY NAME -------------------- */}
                            {state.enabled_company_name && (
                                <CompanyNameValue
                                    value={subscription.company_name}
                                />
                            )}
                            {/* -------------------- MOBILE PHONE -------------------- */}
                            {state.enabled_mobile_phone && (
                                <MobilePhoneValue
                                    value={subscription.user?.mobile_phone}
                                />
                            )}
                            {/* -------------------- EMAIL -------------------- */}
                            {state.enabled_email && (
                                <EmailValue
                                    value={subscription.user?.email}
                                />
                            )}
                            {/* -------------------- FRENCH DEPARTMENT -------------------- */}
                            {state.enabled_french_department && (
                                <FrenchDepartmentValue
                                    value={subscription.user?.department ? (subscription.user.department?.name + ' (' + subscription.user.department?.number + ')') : ''}
                                />
                            )}
                            {/* -------------------- CITY -------------------- */}
                            {state.enabled_city && (
                                <CityValue
                                    value={subscription.city}
                                />
                            )}
                            {/* -------------------- POSTAL CODE -------------------- */}
                            {state.enabled_postal_code && (
                                <PostalCodeValue
                                    value={subscription.postal_code}
                                />
                            )}
                            {/* -------------------- ADDRESS -------------------- */}
                            {state.enabled_address && (
                                <AddressValue
                                    value={subscription.address}
                                />
                            )}
                            {/* -------------------- ADDRESS DETAILS -------------------- */}
                            {state.enabled_address_details && (
                                <AddressDetailsValue
                                    value={subscription.address_details}
                                />
                            )}
                            {/* -------------------- PAYS -------------------- */}
                            {state.enabled_country && (
                                <CountryValue
                                    value={subscription.country?.name}
                                />
                            )}
                            {/* -------------------- PHONE -------------------- */}
                            {state.enabled_phone && (
                                <PhoneValue
                                    value={subscription.user?.phone}
                                />
                            )}
                            {/* -------------------- OTHER PHONE -------------------- */}
                            {state.enabled_other_phone && (
                                <OtherPhoneValue
                                    value={subscription.user?.other_phone}
                                />
                            )}
                            {/* -------------------- SUBSCRIPTION EMAIL -------------------- */}
                            {state.enabled_subscription_email && (
                                <div className="column-list">
                                    {subscription.email}
                                </div>
                            )}
                            {/* -------------------- START DATE -------------------- */}
                            {state.enabled_start_date && (
                                <div
                                    className="column-list"
                                    style={{ width: 120 }}
                                >
                                    {subscription.start_date ? moment(subscription.start_date).format('DD/MM/YYYY') : ''}
                                </div>
                            )}
                            {/* -------------------- SUBSCRIPTION EXPIRATION -------------------- */}
                            {state.enabled_subscription_expiration && (
                                <div 
                                    className="column-list"
                                    style={{ width: 120 }}
                                >
                                    {subscription.end_date ? getFormattedDate(subscription.end_date) : ''}
                                </div>
                            )}
                            {/* -------------------- MONTH YEAR SUBSCRIPTION EXPIRATION -------------------- */}
                            {state.enabled_month_year_subscription_expiration && (
                                <div className="column-list">
                                    {subscription.end_date ? getMonthYearDate(subscription.end_date) : ''}
                                </div>
                            )}
                            {/* -------------------- USER CATEGORY -------------------- */}
                            {state.enabled_user_category && (
                                <UserCategoryValue
                                    value={subscription.user?.userCategories?.map((c, index) => {
                                        if (index === 0) return c.name;
                                        return ', ' + c.name;
                                    })}
                                />
                            )}
                            {/* -------------------- BIRTH DATE -------------------- */}
                            {state.enabled_birth_date && (
                                <BirthDateValue
                                    value={getFormattedDate(subscription.user?.birth_date)}
                                />
                            )}
                            {/* -------------------- FEDERATED ASSOCIATION -------------------- */}
                            {state.enabled_federated_association && (
                                <div className="column-list">
                                    {(subscription.user && subscription.user.federatedAssociation) ? subscription.user.federatedAssociation.name : ''}
                                </div>
                            )}
                            {/* -------------------- MEMBERSHIP EXPIRATION -------------------- */}
                            {state.enabled_membership_expiration && (
                                <div className="column-list">
                                    {(subscription.user && subscription.user.membershipExpiration) ? getFormattedDate(subscription.user.membershipExpiration) : ''}
                                </div>
                            )}
                            {/* -------------------- MEMBERSHIP EMAIL -------------------- */}
                            {state.enabled_membership_email && (
                                <div className="column-list">
                                    {subscription.user ? subscription.user.membershipEmail : ''}
                                </div>
                            )}
                            {/* -------------------- BILL EMAIL -------------------- */}
                            {state.enabled_bill_email && (
                                <div className="column-list">
                                    {subscription.user ? subscription.user.billEmail : ''}
                                </div>
                            )}
                            {/* -------------------- ADHERENT TYPE -------------------- */}
                            {state.enabled_adherent_type && (
                                <div className="column-list">
                                    {subscription.user ? subscription.user.adherentType : ''}
                                </div>
                            )}
                            {/* -------------------- LAST NUMBER RECEIVED -------------------- */}
                            {state.enabled_last_number_received && (
                                <div className="column-list">
                                    {subscription.lastNumberReceived}
                                </div>
                            )}
                            {/* -------------------- LAST NUMBER TO RECEIVED -------------------- */}
                            {state.enabled_last_number_to_receive && (
                                <div className="column-list">
                                    {subscription.lastNumberToReceive}
                                </div>
                            )}
                            {/* -------------------- CREATED AT -------------------- */}
                            {state.enabled_created_at && (
                                <div className="column-list">
                                    {getFormattedCreatedAt(subscription.created_at)}
                                </div>
                            )}
                            {/* -------------------- BILL STATE -------------------- */}
                            {state.enabled_bill_state && (
                                <div className="column-list">
                                    {getLabelStatus(billStates, subscription.bill_state)}
                                </div>
                            )}
                            {/* -------------------- BILL REFERENCE -------------------- */}
                            {state.enabled_bill_reference && (
                                <div className="column-list">
                                    {subscription.bill_reference}
                                </div>
                            )}
                            {/* -------------------- STATUS -------------------- */}
                            {state.enabled_bill_status && (
                                <div className="column-list">
                                    <b style={{ color: getColorStatus(billStatuses, subscription.bill_status) }}>
                                        {getLabelStatus(billStatuses, subscription.bill_status)}
                                    </b>
                                </div>
                            )}
                            {/* -------------------- BILL TOTAL AMOUNT -------------------- */}
                            {state.enabled_bill_total_amount && (
                                <div className="column-list">
                                    {getAmountTTC(subscription.bill_total_amount)}
                                </div>
                            )}
                            {/* -------------------- IS ALTERRENAT -------------------- */}
                            {state.enabled_is_alterrenat && (
                                <div className="column-list">
                                    {subscription.is_alterrenat ? 'Oui' : 'Non'}
                                </div>
                            )}
                            {/* -------------------- RENEW STATUS -------------------- */}
                            {state.enabled_renew_status && (
                                <div className="column-list">
                                    {subscription.renewStatus === 'renewed' ? 'Renouvelé' : 'Non renouvelé'}
                                </div>
                            )}
                            {/* -------------------- ORIGIN -------------------- */}
                            {state.enabled_origin && (
                                <div
                                    className="column-list"
                                    style={{ width: 150 }}
                                >
                                    {getSubscriptionOriginLabel(subscription.origin)}
                                </div>
                            )}
                        </div>
                        <br />
                    </Fragment>
                ))
            )}
            {/* -------------------- UPDATE SUBSCRIPTION -------------------- */}
            {state.openUpdate && (
                <UpdateSubscription
                    subscription={state.subscription}
                    onClose={() => setState({ ...state, openUpdate: false })}
                    refetch={() => setState({ ...state, openUpdate: false, loading: true })}
                />
            )}
            {/* -------------------- STOP SUBSCRIPTION -------------------- */}
            {state.openStop && (
                <StopSubscription
                    subscription={state.subscription}
                    onClose={() => setState({ ...state, openStop: false })}
                    refetch={() => setState({ ...state, openStop: false, loading: true })}
                />
            )}
        </>
    );
};

// ----------------------------------------------------------------------------------------------- \\
// ---------------------------------------- MANAGE COLUMNS --------------------------------------- \\
// ----------------------------------------------------------------------------------------------- \\

const ManageColumns = ({ state, setState, storedUserId }) => {
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {

        const columns = {
            enabled_subscription_id: state.enabled_subscription_id,
            enabled_subscription_status: state.enabled_subscription_status,
            enabled_client_code: state.enabled_client_code,
            enabled_external_client_code: state.enabled_external_client_code,
            enabled_subscription_type: state.enabled_subscription_type,
            enabled_membership_status: state.enabled_membership_status,
            enabled_last_name: state.enabled_last_name,
            enabled_first_name: state.enabled_first_name,
            enabled_company_name: state.enabled_company_name,
            enabled_mobile_phone: state.enabled_mobile_phone,
            enabled_email: state.enabled_email,
            enabled_french_department: state.enabled_french_department,
            enabled_city: state.enabled_city,
            enabled_postal_code: state.enabled_postal_code,
            enabled_address: state.enabled_address,
            enabled_address_details: state.enabled_address_details,
            enabled_country: state.enabled_country,
            enabled_phone: state.enabled_phone,
            enabled_other_phone: state.enabled_other_phone,
            enabled_subscription_email: state.enabled_subscription_email,
            enabled_subscription_expiration: state.enabled_subscription_expiration,
            enabled_month_year_subscription_expiration: state.enabled_month_year_subscription_expiration,
            enabled_user_category: state.enabled_user_category,
            enabled_birth_date: state.enabled_birth_date,
            enabled_federated_association: state.enabled_federated_association,
            enabled_membership_expiration: state.enabled_membership_expiration,
            enabled_membership_email: state.enabled_membership_email,
            enabled_bill_email: state.enabled_bill_email,
            enabled_adherent_type: state.enabled_adherent_type,
            enabled_last_number_received: state.enabled_last_number_received,
            enabled_last_number_to_receive: state.enabled_last_number_to_receive,
            enabled_created_at: state.enabled_created_at,
            enabled_bill_state: state.enabled_bill_state,
            enabled_bill_reference: state.enabled_bill_reference,
            enabled_bill_status: state.enabled_bill_status,
            enabled_bill_total_amount: state.enabled_bill_total_amount,
            enabled_is_alterrenat: state.enabled_is_alterrenat,
            enabled_renew_status: state.enabled_renew_status,
            enabled_origin: state.enabled_origin,
            enabled_start_date: state.enabled_start_date,
        };

        if (state.loadingColumns) {
            updateIndexPreferences(storedUserId, {
                name: 'subscriptions',
                columns: JSON.stringify(columns),
            }).then(res => {
                if (res.status === 200) {
                    enqueueSnackbar(res.data.message, { variant: 'success' });
                    setState({ ...state, loadingColumns: false, openColumns: false });
                }
                else {
                    enqueueSnackbar(res, { variant: 'error' });
                    setState({ ...state, loadingColumns: false });
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.loadingColumns]);

    return (
        <Dialog open>
            <DialogContent style={{ paddingBottom: 20 }}>
                <p className="dialog-title">
                    Gérer les colonnes affichées
                </p>
                <div style={{ display: 'flex' }}>
                    <div>
                        {/* -------------------- SUBSCRIPTION ID -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_subscription_id}
                                onChange={e => setState({ ...state, enabled_subscription_id: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            N°Abo
                        </Grid>
                        {/* -------------------- SUBSCRIPTION STATUS -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_subscription_status}
                                onChange={e => setState({ ...state, enabled_subscription_status: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Statut abonnement
                        </Grid>
                        {/* -------------------- CLIENT CODE -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_client_code}
                                onChange={e => setState({ ...state, enabled_client_code: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Code client
                        </Grid>
                        {/* -------------------- EXTERNAL CLIENT CODE -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_external_client_code}
                                onChange={e => setState({ ...state, enabled_external_client_code: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Code client externe
                        </Grid>
                        {/* -------------------- SUBSCRIPTION TYPE -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_subscription_type}
                                onChange={e => setState({ ...state, enabled_subscription_type: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Type d'abonnement
                        </Grid>
                        {/* -------------------- MEMBERSHIP STATUS -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_membership_status}
                                onChange={e => setState({ ...state, enabled_membership_status: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Statut adhésion
                        </Grid>
                        {/* -------------------- LAST NAME -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_last_name}
                                onChange={e => setState({ ...state, enabled_last_name: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Nom
                        </Grid>
                        {/* -------------------- FIRST NAME -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_first_name}
                                onChange={e => setState({ ...state, enabled_first_name: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Prénom
                        </Grid>
                        {/* -------------------- COMPANY NAME -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_company_name}
                                onChange={e => setState({ ...state, enabled_company_name: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Nom de l'entreprise
                        </Grid>
                        {/* -------------------- MOBILE PHONE -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_mobile_phone}
                                onChange={e => setState({ ...state, enabled_mobile_phone: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Téléphone portable
                        </Grid>
                        {/* -------------------- EMAIL -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_email}
                                onChange={e => setState({ ...state, enabled_email: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Email
                        </Grid>
                        {/* -------------------- FRENCH DEPARTMENT -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_french_department}
                                onChange={e => setState({ ...state, enabled_french_department: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Département
                        </Grid>
                        {/* -------------------- CITY -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_city}
                                onChange={e => setState({ ...state, enabled_city: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Ville
                        </Grid>
                        {/* -------------------- POSTAL CODE -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_postal_code}
                                onChange={e => setState({ ...state, enabled_postal_code: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Code postal
                        </Grid>
                        {/* -------------------- ADDRESS -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_address}
                                onChange={e => setState({ ...state, enabled_address: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Adresse ABO (n° et voie)
                        </Grid>
                        {/* -------------------- ADDRESS DETAILS -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_address_details}
                                onChange={e => setState({ ...state, enabled_address_details: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Adresse ABO (complément)
                        </Grid>
                        {/* -------------------- COUNTRY -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_country}
                                onChange={e => setState({ ...state, enabled_country: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Pays
                        </Grid>
                        {/* -------------------- PHONE -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_phone}
                                onChange={e => setState({ ...state, enabled_phone: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Téléphone fixe
                        </Grid>
                        {/* -------------------- OTHER PHONE -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_other_phone}
                                onChange={e => setState({ ...state, enabled_other_phone: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Autre téléphone
                        </Grid>
                        {/* -------------------- SUBSCRIPTION EMAIL -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_subscription_email}
                                onChange={e => setState({ ...state, enabled_subscription_email: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Email abonnement
                        </Grid>
                    </div>
                    <div>
                        {/* -------------------- START DATE -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_start_date}
                                onChange={e => setState({ ...state, enabled_start_date: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Date début ABO
                        </Grid>
                        {/* -------------------- SUBSCRIPTION EXPIRATION -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_subscription_expiration}
                                onChange={e => setState({ ...state, enabled_subscription_expiration: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Date expiration ABO
                        </Grid>
                        {/* -------------------- MONTH YEAR SUBSCRIPTION EXPIRATION -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_month_year_subscription_expiration}
                                onChange={e => setState({ ...state, enabled_month_year_subscription_expiration: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Mois / Année d'expiration ABO
                        </Grid>
                        {/* -------------------- USER CATEGORY -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_user_category}
                                onChange={e => setState({ ...state, enabled_user_category: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Rôle
                        </Grid>
                        {/* -------------------- BIRTH DATE -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_birth_date}
                                onChange={e => setState({ ...state, enabled_birth_date: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Date de naissance
                        </Grid>
                        {/* -------------------- FEDERATED ASSOCIATION -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_federated_association}
                                onChange={e => setState({ ...state, enabled_federated_association: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Association fédérée
                        </Grid>
                        {/* -------------------- MEMBERSHIP EXPIRATION -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_membership_expiration}
                                onChange={e => setState({ ...state, enabled_membership_expiration: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Date d'expiration adhé.
                        </Grid>
                        {/* -------------------- MEMBERSHIP EMAIL -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_membership_email}
                                onChange={e => setState({ ...state, enabled_membership_email: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Email adhésion
                        </Grid>
                        {/* -------------------- BILL EMAIL -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_bill_email}
                                onChange={e => setState({ ...state, enabled_bill_email: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Email bon de commande
                        </Grid>
                        {/* -------------------- ADHERENT TYPE -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_adherent_type}
                                onChange={e => setState({ ...state, enabled_adherent_type: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Type d'adhérent
                        </Grid>
                        {/* -------------------- LAST NUMBER RECEIVED -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_last_number_received}
                                onChange={e => setState({ ...state, enabled_last_number_received: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Dernier n° reçu
                        </Grid>
                        {/* -------------------- LAST NUMBER TO RECEIVED -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_last_number_to_receive}
                                onChange={e => setState({ ...state, enabled_last_number_to_receive: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Abonné jusqu'au n°
                        </Grid>
                        {/* -------------------- CREATED AT -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_created_at}
                                onChange={e => setState({ ...state, enabled_created_at: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Date BC
                        </Grid>
                        {/* -------------------- BILL STATE -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_bill_state}
                                onChange={e => setState({ ...state, enabled_bill_state: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Etat BC
                        </Grid>
                        {/* -------------------- BILL REFERENCE -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_bill_reference}
                                onChange={e => setState({ ...state, enabled_bill_reference: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Référence BC
                        </Grid>
                        {/* -------------------- BILL STATUS -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_bill_status}
                                onChange={e => setState({ ...state, enabled_bill_status: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Statut BC
                        </Grid>
                        {/* -------------------- BILL TOTAL AMOUNT -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_bill_total_amount}
                                onChange={e => setState({ ...state, enabled_bill_total_amount: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Montant total BC
                        </Grid>
                        {/* -------------------- IS ALTERRENAT -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_is_alterrenat}
                                onChange={e => setState({ ...state, enabled_is_alterrenat: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Alterrenat
                        </Grid>
                        {/* -------------------- RENEW STATUS -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_renew_status}
                                onChange={e => setState({ ...state, enabled_renew_status: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Statut renouvellement
                        </Grid>
                        {/* -------------------- ORIGIN -------------------- */}
                        <Grid container alignItems="center">
                            <Checkbox
                                checked={state.enabled_origin}
                                onChange={e => setState({ ...state, enabled_origin: e.target.checked })}
                                style={{ padding: 6 }}
                            />
                            Origine
                        </Grid>
                    </div>
                </div>
            </DialogContent>
            <Divider />
            <DialogActions style={{ justifyContent: 'space-between' }}>
                <Button onClick={() => setState({ ...state, openColumns: false })} variant="contained">
                    Fermer
                </Button>
                <Button onClick={() => setState({ ...state, loadingColumns: true })} variant="contained">
                    Enregistrer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ----------------------------------------------------------------------------------------------- \\
// -------------------------------------- SUBSCRIPTION LINKS ------------------------------------- \\
// ----------------------------------------------------------------------------------------------- \\

const SubscriptionLinks = ({ state, setState }) => {
    const history = useHistory();
    const storedUser = getStoredUser();

    return (
        <Dialog open>
            <DialogContent style={{ paddingBottom: 20, display: 'flex' }}>
                <div
                    onClick={() => setState({ ...state, openUpdate: true, openLinks: false })}
                    className="subscription-links"
                >
                    Modifier l'abonnement
                </div>
                {(storedUser?.role?.id < 4 && (
                    state.subscription?.status === 'validated'
                    || state.subscription?.status === 'expired'
                    || state.subscription?.status === 'stopped'
                )) && (
                    <div
                        onClick={() => setState({ ...state, openStop: true, openLinks: false })}
                        className="subscription-links"
                        style={{ marginLeft: 24 }}
                    >
                        {(state.subscription?.status === 'validated' || state.subscription?.status === 'expired') && (
                            "Stopper l'abonnement"
                        )}
                        {state.subscription?.status === 'stopped' && (
                            "Reprendre l'abonnement"
                        )}
                    </div>
                )}
                <div
                    onClick={() => history.push('/management/subscriptions/' + state.billId)}
                    className="subscription-links"
                    style={{ marginLeft: 24 }}
                >
                    Accéder au bon de commande
                </div>
            </DialogContent>
            <Divider />
            <DialogActions style={{ justifyContent: 'center' }}>
                <Button onClick={() => setState({ ...state, openLinks: false })} variant="contained">
                    Fermer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SubscriptionsRouter;
